import { useState, useContext } from 'react';
import userApi from '../utils/api/user';
import AuthenticationContext from '../contexts/AuthenticationContext';
import AppContext from '../contexts/AppContext';

interface TenantRedirectData {
  tenantId: number;
  redirectPath: string;
  email: string;
}

export const useTenantRedirect = () => {
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [mfaData, setMfaData] = useState<TenantRedirectData | null>(null);
  const authStore = useContext(AuthenticationContext);
  const appContext = useContext(AppContext);
  const user = appContext.get('user');

  const redirectToTenant = async (tenantId: number, redirectPath: string = '/') => {
    try {
      const data = await userApi.redirectTenant(tenantId);

      if (data.mfa_required) {
        // MFA is required, show modal
        setMfaData({
          tenantId,
          redirectPath,
          email: user?.email || '',
        });
        setShowMFAModal(true);
        return null;
      }

      // No MFA required, redirect immediately
      const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;

      if (startUrl && data.tenantName) {
        const url = startUrl.split('//');

        if (url) {
          window.location.href =
            url[0] +
            `//${data.tenantName}.${url[1]}/identity-auth?to=${redirectPath}`;
        }
      }

      return data;
    } catch (error) {
      console.error('Error redirecting to tenant:', error);
      throw error;
    }
  };

  const handleMFAVerificationSuccess = async () => {
    if (mfaData) {
      const { tenantId, redirectPath } = mfaData;

      try {
        // Call redirectTenant again after successful MFA verification to get the token
        const data = await userApi.redirectTenant(tenantId);

        const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;

        if (startUrl && data.tenantName) {
          const url = startUrl.split('//');

          if (url) {
            window.location.href =  
              url[0] +
              `//${data.tenantName}.${url[1]}/identity-auth?to=${redirectPath}`;

          }
        }
      } catch (error) {
        console.error('Error redirecting to tenant after MFA verification:', error);
        throw error;
      }
    }
  };

  const handleMFAModalClose = () => {
    setShowMFAModal(false);
    setMfaData(null);
  };

  return {
    redirectToTenant,
    showMFAModal,
    mfaData,
    handleMFAVerificationSuccess,
    handleMFAModalClose,
    authStore,
  };
};
